import { views } from '@/copy';
import type { EntityDataInput, ExcludeMatch, ExcludeTerm } from '../types';

// Must stay in step with the backend's entity_data.MAX_EXCLUDE_TERMS.
export const MAX_EXCLUDE_TERMS = 20;

export const EXCLUDE_MATCH_MODES: readonly ExcludeMatch[] = ['is', 'contains'];
export const DEFAULT_EXCLUDE_MATCH: ExcludeMatch = 'is';

// Must stay in step with the backend's name_filters.
export const MAX_TERM_LENGTH = 100;
export const MIN_CONTAINS_CHARS = 2;

/** The react-query key and the report's data hash are keyed on args, so an
 * unsorted list forks both. Codepoint order, not `localeCompare`, to match the
 * backend's `sorted()`. */
export const canonicalExcludeTerms = (
    terms: readonly ExcludeTerm[],
): ExcludeTerm[] => {
    const byPair = new Map<string, ExcludeTerm>();
    for (const term of terms) {
        const match = term?.match ?? DEFAULT_EXCLUDE_MATCH;
        const value = (term?.value ?? '').trim().toLowerCase();
        if (value) byPair.set(`${match}\u0000${value}`, { match, value });
    }
    return [...byPair.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, term]) => term);
};

export const formatExcludeTerm = ({ match, value }: ExcludeTerm): string =>
    match === 'contains'
        ? views.assistantVisuals.excludeTermContains(value)
        : value;

/**
 * These live here, not in `visuals/index.ts`: a table importing from `index`
 * would close an import cycle.
 */

/** Anchor-less means no type AND no anchor id — a typeless input still carrying
 * `entity_index` (chat) or `entity_id` (report args) is an anchored call the
 * model forgot to type. */
export const isStandaloneListInput = (input: EntityDataInput): boolean =>
    (input?.data_source ?? 'pop_index') === 'pop_index' &&
    (input?.entity_relationship === 'brands' ||
        input?.entity_relationship === 'categories') &&
    !input?.entity_type &&
    input?.entity_index == null &&
    !input?.entity_id;

/** Deliberately says nothing about an anchor: a hashtag's metrics are the same
 * in or out of a category. */
export const isPinnedHashtagsInput = (input: EntityDataInput): boolean =>
    input?.data_source === 'tiktok' &&
    input?.entity_relationship === 'hashtags' &&
    (input?.hashtags?.length ?? 0) > 0;

/** Report args only — chat inputs carry `entity_indices`, already enforced
 * backend-side. */
export const isChartableListInput = (input: EntityDataInput): boolean =>
    (isStandaloneListInput(input) && (input?.entity_ids?.length ?? 0) > 0) ||
    isPinnedHashtagsInput(input);

/** The tables' matchers negate this so exactly one widget matches a config:
 * chat renders EVERY match, and two would stack. */
export const selectsChartView = (input: EntityDataInput): boolean =>
    isChartableListInput(input) &&
    Boolean(input?.aggregate || input?.trend_lines);

/** Twinned by `pinnedEntity` in widgetCatalog.js. */
const PINNABLE_RELATIONSHIPS = new Set([
    'brands',
    'cat5s',
    'related_categories',
    'categories',
]);
export const isPinnableListInput = (input: EntityDataInput): boolean =>
    ((input?.data_source ?? 'pop_index') === 'pop_index' &&
        PINNABLE_RELATIONSHIPS.has(input?.entity_relationship ?? '')) ||
    (input?.data_source === 'tiktok' &&
        input?.entity_relationship === 'hashtags');

/** NOT the complement of `isPinnableListInput`. Two twins have to stay in
 * step: the backend's `_EXCLUDABLE_CALLS` and `widgetCatalog.js`. */
const EXCLUDABLE_POP_INDEX_RELATIONSHIPS = new Set([
    'brands',
    'cat5s',
    'related_categories',
]);
export const isExcludableListInput = (input: EntityDataInput): boolean => {
    const source = input?.data_source ?? 'pop_index';
    const relationship = input?.entity_relationship ?? '';
    if (source === 'pop_index') {
        return (
            EXCLUDABLE_POP_INDEX_RELATIONSHIPS.has(relationship) &&
            !isStandaloneListInput(input)
        );
    }
    if (source === 'tiktok') return relationship === 'hashtags';
    if (source === 'google') return relationship === 'related_terms';
    return false;
};

/** Pins and exclusions are mutually exclusive — the backend 400s on the pair. */
export const hasPinnedList = (input: EntityDataInput): boolean =>
    (input?.entity_ids?.length ?? 0) > 0 || (input?.hashtags?.length ?? 0) > 0;

/** An `entity_ids`-pinned list is deliberately out: those pins are UUIDs and
 * `_rows` carries no id, so nothing on screen says which pin to drop. */
export const canExcludeRows = (input: EntityDataInput): boolean => {
    if (isPinnedHashtagsInput(input)) {
        return (input?.hashtags?.length ?? 0) > 1;
    }
    return (
        isExcludableListInput(input) &&
        !hasPinnedList(input) &&
        (input?.exclude?.length ?? 0) < MAX_EXCLUDE_TERMS
    );
};
